// Regras de VIP derivadas das tags que a Smartico grava em `core_public_tags`
// (ver TUserProfile em types/smartico.d.ts). As tags são "cookies do lado do
// servidor": o CRM liga/desliga sem deploy nosso, e o front só lê.
//
// Este arquivo é o ÚNICO lugar que decide o que é VIP. Layout, chips e qualquer
// variação por tier passam por aqui — não espalhar `includes('VIP_...')` pelos
// componentes.

// Normaliza pra comparar sem depender de como o CRM digitou: `VIP_1`, `vip-1`,
// `VIP 1` e `Vip  1` viram todos "VIP 1".
export function normalizeVipTag(tag?: string | null): string {
  return (tag ?? '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

// Qualquer tag da família VIP conta: `VIP`, `VIP SIGNATURE`, `VIP 1`, `VIP 3`,
// `VIP3`... — assim um tier novo no CRM não exige deploy.
// A fronteira (fim da string, espaço ou dígito) existe pra não capturar palavra
// que só COMEÇA com as três letras: "VIPER", "VIPGRATIS" e afins não são VIP.
const VIP_TAG_PATTERN = /^VIP(?:$|[\s\d])/;

export function isVipTag(tag?: string | null): boolean {
  return VIP_TAG_PATTERN.test(normalizeVipTag(tag));
}

// Só as tags VIP, já normalizadas (o resto de `core_public_tags` é ruído de CRM).
export function extractVipTags(tags?: string[] | null): string[] {
  if (!Array.isArray(tags)) return [];

  return tags.filter(isVipTag).map(normalizeVipTag);
}

// Tier numerado dentro da família VIP: `VIP_3` → 3.
// Tolera a palavra do meio que o CRM às vezes escreve (`VIP_LEVEL_3`,
// `VIP_TIER_3`, `VIP_NIVEL_3`) e a ausência de separador (`VIP3`), porque quem
// digita a tag é o time de CRM e não temos como impor formato.
// Devolve `null` pra tag VIP sem número — `VIP_SIGNATURE` é VIP, mas não é tier,
// e é justamente essa distinção que decide se a arte do card muda.
const VIP_TIER_PATTERN = /^VIP(?:\s(?:LEVEL|TIER|NIVEL))?\s?(\d+)$/;

export function vipTierFromTag(tag?: string | null): number | null {
  const match = VIP_TIER_PATTERN.exec(normalizeVipTag(tag));

  return match ? Number(match[1]) : null;
}

// Tier efetivo do usuário. Com mais de uma tag de tier (acontece quando o CRM
// promove sem limpar a antiga), vale o MAIOR — a promoção é o estado novo, e
// entregar o benefício menor seria rebaixar quem subiu.
export function vipTierFromTags(tags?: string[] | null): number | null {
  if (!Array.isArray(tags)) return null;

  const tiers = tags
    .map(vipTierFromTag)
    .filter((tier): tier is number => tier !== null);

  return tiers.length ? Math.max(...tiers) : null;
}
